import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const AuthPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { validateAnonymousToken } = useAuth();

  const handleGoogleLogin = () => {
    window.location.href = '/.auth/login/google';
  };

  const handleMicrosoftLogin = () => {
    window.location.href = '/.auth/login/aad';
  };
  // disabled for now 
  // const handleFacebookLogin = () => {
  //   window.location.href = '/.auth/login/facebook';
  // };

  // Handle token validation from email link
  useEffect(() => {
    const handleTokenValidation = async () => {
      const token = searchParams.get('token');
      const emailParam = searchParams.get('email');
      
      if (token && emailParam) {
        setLoading(true);
        setMessage('Validation du lien de connexion...');
        
        try {
          const result = await validateAnonymousToken(token, emailParam);
          if (result.success) {
            setMessage('Connexion réussie! Redirection vers le tableau de bord...');
            // Redirect to dashboard after successful authentication
            setTimeout(() => {
              navigate('/dashboard');
            }, 2000);
          } else {
            setMessage(`Erreur de connexion: ${result.error}`);
          }
        } catch (error) {
          setMessage('Erreur lors de la validation du token. Veuillez réessayer.');
        } finally {
          setLoading(false);
        }
      }
    };

    handleTokenValidation();
  }, [searchParams, validateAnonymousToken, navigate]);

  const handleAnonymousLogin = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setMessage('Veuillez saisir une adresse email valide.');
      return;
    }

    setLoading(true);
    try {
      const apiUrl = process.env.NODE_ENV === 'development'
        ? 'http://localhost:7071/api/generateAnonymousToken'
        : '/api/generateAnonymousToken';
        
      const response = await axios.post(apiUrl, {
        email: email.trim()
      });
      
      if (response.data.success) {
        setMessage('Un lien de connexion a été envoyé à votre adresse email. Vérifiez votre boîte de réception.');
        setEmail('');
      } else {
        setMessage('Erreur lors de l\'envoi de l\'email. Veuillez réessayer.');
      }
    } catch (error) {
      setMessage('Erreur lors de l\'envoi de l\'email. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container" style={{ marginTop: '2rem' }}>
      <div className="card" style={{ maxWidth: '500px', margin: '0 auto' }}>
        <h1 style={{ textAlign: 'center', marginBottom: '2rem', color: '#2c3e50' }}>
          Connexion
        </h1>
        <p style={{ textAlign: 'center', marginBottom: '2rem', color: '#7f8c8d' }}>
          Connectez-vous pour sauvegarder votre progression et accéder à vos résultats
        </p>

        {message && (
          <div className={message.includes('Erreur') ? 'error' : 'success'}>
            {message}
          </div>
        )}

        {/* Connexion sociale - Only show in production */}
        {process.env.NODE_ENV !== 'development' && (
          <div className="auth-section" style={{ marginBottom: '2rem' }}>
            <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>
              Connexion avec vos comptes sociaux
            </h3>
            <div className="auth-buttons">
              <button 
                onClick={handleGoogleLogin}
                className="btn btn-primary"
                style={{ backgroundColor: '#db4437' }}
              >
                🔗 Se connecter avec Google
              </button>
              <button 
                onClick={handleMicrosoftLogin}
                className="btn btn-primary"
                style={{ backgroundColor: '#0078d4' }}
              >
                🔗 Se connecter avec Microsoft
              </button>
            </div>
          </div>
        )}

        {/* Development notice */}
        {process.env.NODE_ENV === 'development' && (
          <div className="auth-section" style={{ marginBottom: '2rem' }}>
            <div style={{ 
              padding: '1rem', 
              backgroundColor: '#fff3cd', 
              border: '1px solid #ffeaa7',
              borderRadius: '4px',
              color: '#856404'
            }}>
              <p style={{ margin: 0, fontSize: '0.9rem' }}>
                <strong>Mode développement:</strong> La connexion OAuth (Google/Microsoft) n'est disponible qu'en production. 
                Utilisez la connexion anonyme par email pour tester localement.
              </p>
            </div>
          </div>
        )}

        {/* Séparateur - Only show in production */}
        {process.env.NODE_ENV !== 'development' && (
          <div style={{ 
            textAlign: 'center', 
            margin: '2rem 0', 
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '50%',
              left: 0,
              right: 0,
              height: '1px',
              backgroundColor: '#ddd'
            }
          }}>
            <span style={{ 
              backgroundColor: 'white', 
              padding: '0 1rem', 
              color: '#7f8c8d',
              position: 'relative',
              zIndex: 1
            }}>
              ou
            </span>
          </div>
        )}

        {/* Connexion anonyme */}
        <div className="auth-section">
          <h3 style={{ marginBottom: '1rem', color: '#2c3e50' }}>
            {process.env.NODE_ENV === 'development' ? 'Connexion par email' : 'Connexion anonyme par email'}
          </h3>
          <p style={{ fontSize: '0.9rem', marginBottom: '1rem', color: '#7f8c8d' }}>
            Saisissez votre email pour recevoir un lien de connexion temporaire
          </p>
          <form onSubmit={handleAnonymousLogin}>
            <div className="form-group">
              <label htmlFor="email" className="form-label">
                Adresse email
              </label>
              <input
                type="email"
                id="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
              />
            </div>
            <button 
              type="submit" 
              className="btn btn-success"
              style={{ width: '100%' }}
              disabled={loading}
            >
              {loading ? 'Envoi en cours...' : '📧 Envoyer le lien de connexion'}
            </button>
          </form>
        </div>

        <div style={{ marginTop: '2rem', textAlign: 'center', fontSize: '0.9rem', color: '#7f8c8d' }}>
          <p>
            En vous connectant, vous acceptez que vos réponses soient sauvegardées 
            de manière sécurisée pour vous permettre de reprendre vos questionnaires.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AuthPage;
